import { useMemo } from 'react';
import { useFuelTypesByIds, useTransmissionsByIds } from '@/features/vehiclesearch/hooks/useVehicleNameLookups';
import type { SelectFilterOption } from '@/features/vehiclesearch/types/manualsearch.types';
import { buildRestrictionOptions, savedIdNeedsResolving } from '../utils/vehicleRestrictionOptions';

interface Params {
    transmissionOptions: SelectFilterOption[];
    fuelTypeOptions: SelectFilterOption[];
    savedTransmissionId: string;
    savedFuelTypeId: string;
    isLoadingTransmissions: boolean;
    isLoadingFuelTypes: boolean;
}

// Resolves a saved id the active list omits through the by-ids endpoints, which — unlike the
// active lists — are not filtered by is_active.
export function useVehicleRestrictionOptions({
    transmissionOptions,
    fuelTypeOptions,
    savedTransmissionId,
    savedFuelTypeId,
    isLoadingTransmissions,
    isLoadingFuelTypes,
}: Params) {
    const resolveTransmission = savedIdNeedsResolving(savedTransmissionId, transmissionOptions, isLoadingTransmissions);
    const resolveFuelType = savedIdNeedsResolving(savedFuelTypeId, fuelTypeOptions, isLoadingFuelTypes);

    const { data: savedTransmissions } = useTransmissionsByIds(resolveTransmission ? [savedTransmissionId] : []);
    const { data: savedFuelTypes } = useFuelTypesByIds(resolveFuelType ? [savedFuelTypeId] : []);

    const transmissionSelectOptions = useMemo(
        () => buildRestrictionOptions(transmissionOptions, savedTransmissionId, savedTransmissions),
        [transmissionOptions, savedTransmissionId, savedTransmissions],
    );

    const fuelTypeSelectOptions = useMemo(
        () => buildRestrictionOptions(fuelTypeOptions, savedFuelTypeId, savedFuelTypes),
        [fuelTypeOptions, savedFuelTypeId, savedFuelTypes],
    );

    return { transmissionSelectOptions, fuelTypeSelectOptions };
}
